import { Link, Navigate, useParams } from 'react-router-dom'
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb'
import { SubscribeCta } from '@/components/website/SubscribeCta'
import { Seo } from '@/components/seo/Seo'
import { site } from '@/data/site'
import { useWebsiteService, useWebsiteServices } from '@/services/httpServices/queries/useWebsite'

/** Plain-text version of the rich description, for the SEO snippet. */
function plainText(value: string | undefined): string {
  return (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function ServiceDetail() {
  const { slug } = useParams()
  const { service, isLoading } = useWebsiteService(slug)
  const { services } = useWebsiteServices()

  if (!service) {
    if (isLoading) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center">
          <i className="fa-solid fa-circle-notch fa-spin text-3xl text-primary" aria-label="Loading" />
        </div>
      )
    }
    return <Navigate to="/services" replace />
  }

  return (
    <>
      <Seo
        override={{
          title: service.metaTitle?.trim() || `${service.title} | ${site.name}`,
          description: service.metaDescription?.trim() || plainText(service.description),
          image: service.ogImage?.trim() || service.image || undefined,
          canonical: service.canonicalUrl?.trim() || undefined,
          noIndex: service.noIndex,
          type: 'article',
        }}
      />

      <PageBreadcrumb
        title={service.title}
        items={[{ label: 'Home', to: '/' }, { label: 'Services', to: '/services' }, { label: service.title }]}
      />

      <section className="service-single-section pt-100 md-pt-80 pb-100 md-pb-80">
        <div className="container">
          <div className="row flex-lg-row-reverse">
            <div className="col-xl-8 col-md-12">
              <div className="service-single-post">
                <div className="service-single-media">
                  <figure className="image-anime">
                    <img src={service.image} alt={`${service.title} image`} />
                  </figure>
                </div>
                <div className="service-single-contain">
                  <div className="service-entry-content">
                    <h2>{service.title}</h2>
                    {service.categories && service.categories.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {service.categories.map((category) => (
                          <span
                            key={category.id || category.slug || category.name}
                            className="d-inline-flex align-items-center gap-1"
                            style={{ fontSize: 13, color: '#6b7280' }}
                          >
                            <i className="fa-solid fa-folder" aria-hidden="true"></i>
                            {category.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {service.overview.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}

                    <h3 className="pt-10">Comprehensive Medical Assessment at Fast Aid Hospital</h3>
                    <p>
                      Every visit begins with a detailed consultation and physical examination. For{' '}
                      {service.title.toLowerCase()} we use on-site laboratory, ECG, ultrasound and X-ray
                      services to reach an accurate diagnosis quickly, so your treatment can start without
                      unnecessary delay.
                    </p>

                    <div className="row gy-4 mb-20">
                      {service.gallery.map((image, index) => (
                        <div className="col-sm-6" key={image}>
                          <div className="service-gallery">
                            <figure className="image-anime">
                              <img src={image} alt={`${service.title} gallery image ${index + 1}`} />
                            </figure>
                          </div>
                        </div>
                      ))}
                    </div>

                    <p>
                      Our clinical team follows standard treatment protocols and explains every step in simple
                      language. When advanced or specialised care is needed, we coordinate a safe referral so
                      you always know what happens next.
                    </p>

                    <h3 className="pt-10">The benefits of {service.title}</h3>
                    <div className="check-list mb-30">
                      <ul>
                        {service.benefits.map((benefit) => (
                          <li key={benefit}>{benefit}</li>
                        ))}
                      </ul>
                    </div>
                    <p className="mb-0">
                      Fast Aid Hospital, Chauddagram is open 24/7 for emergencies, with outdoor consultation
                      from 8:00 AM to 8:00 PM. Call {site.phone} to book an appointment or ask about{' '}
                      {service.title.toLowerCase()}.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-md-12">
              <div className="widget-sidebar">
                {/* widget categories list */}
                <div className="widget widget-categories-list">
                  <div className="widget-title">
                    <h3>Categories</h3>
                  </div>
                  <div className="widget-content">
                    <ul className="category-list">
                      {services.map((item) => (
                        <li key={item.slug}>
                          <Link
                            to={`/services/${item.slug}`}
                            className={item.slug === service.slug ? 'active' : undefined}
                          >
                            <span className="categories-name">{item.title}</span>{' '}
                            <span className="categories-count">({item.count})</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* widget company profile */}
                <div className="widget widget-company-profile">
                  <div className="widget-title">
                    <h3>Company Profile</h3>
                  </div>
                  <div className="widget-content">
                    <ul className="company-profile-list">
                      <li>
                        <a href="#">
                          <span className="company-profile-icon d-flex align-items-center justify-content-center flex-shrink-0">
                            <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
                          </span>
                          <span className="download-content">Download PDF File</span>
                        </a>
                      </li>
                      <li>
                        <a href="#">
                          <span className="company-profile-icon d-flex align-items-center justify-content-center flex-shrink-0">
                            <i className="fa-solid fa-file-word" aria-hidden="true"></i>
                          </span>
                          <span className="download-content">Download Word File</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* widget cta */}
                <div className="widget widget-cta">
                  <div className="widget-title">
                    <h3>Needs Any Help?</h3>
                  </div>
                  <div className="widget-content">
                    <p>
                      Our care team is available 24/7 for emergencies and can answer your questions about{' '}
                      {service.title.toLowerCase()} or help you book an appointment.
                    </p>
                    <div className="service-cta-item">
                      <div className="service-cta-list d-flex gap-3">
                        <div className="service-cta-icon d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="fa-solid fa-phone-volume"></i>
                        </div>
                        <div className="service-cta-content flex-grow-1">
                          <p>Phone Number</p>
                          <a href={`tel:${site.phone}`}>{site.phone}</a>
                        </div>
                      </div>
                      <div className="service-cta-list d-flex gap-3">
                        <div className="service-cta-icon d-flex align-items-center justify-content-center flex-shrink-0">
                          <i className="fa-solid fa-envelope"></i>
                        </div>
                        <div className="service-cta-content flex-grow-1">
                          <p>Email Address</p>
                          <a href={`mailto:${site.email}`}>{site.email}</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SubscribeCta />
    </>
  )
}
